/**
 * SG-14632 — what the trial Checkout told us before the redirect, carried to the
 * page Stripe sends the customer back to: whether the first payment happens today
 * (`chargeToday`) and when it is due otherwise (`firstChargeAt`, epoch seconds).
 *
 * localStorage, not sessionStorage: inside the Sheets add-on Checkout opens in a new
 * tab (Stripe refuses to be framed) and a noopener tab does not inherit the opener's
 * sessionStorage. The record is one-shot (read removes it), expires after
 * `TRIAL_CHECKOUT_RETURN_MAX_AGE_MS`, and is dropped on a cancelled return and at
 * the start of every new attempt, so it can never colour an unrelated Checkout.
 */
export const TRIAL_CHECKOUT_RETURN_KEY = 'sgTrialCheckoutReturn'

/**
 * The `source` the trial Checkout puts on its success URL (`?checkout=success&source=trial_checkout`).
 * Only a return carrying it may use the stored record. Core must add it to its allow-listed checkout sources.
 */
export const TRIAL_CHECKOUT_SOURCE = 'trial_checkout'

/** How long a stored record stays valid. A Checkout visit that takes longer is treated as abandoned. */
export const TRIAL_CHECKOUT_RETURN_MAX_AGE_MS = 60 * 60 * 1000

/**
 * Reads the Checkout outcome core puts on the success URL
 * (`?checkout=success&source=trial_checkout&chargeToday=1&firstChargeAt=<epoch seconds>`).
 * Inside the Sheets add-on the Checkout tab cannot read what the framed app stored, so the
 * values ride the URL; they are display-only, and anything malformed reads as no record
 * (the stored record, if any, is the fallback).
 *
 * @param {string} [search] - The return URL's query string.
 * @returns {{chargeToday: boolean, firstChargeAt: (number|null)}|null} The record, or null.
 */
export const readTrialCheckoutReturnFromSearch = (search) => {
    try {
        const params = new URLSearchParams(search || '')
        const chargeToday = params.get('chargeToday')
        if (chargeToday !== '0' && chargeToday !== '1') return null
        const rawFirstCharge = params.get('firstChargeAt')
        const firstChargeAt = rawFirstCharge && /^\d{9,11}$/.test(rawFirstCharge) ? Number(rawFirstCharge) : null
        // A future-dated first payment (not charged today) needs its date to say anything useful.
        if (chargeToday === '0' && firstChargeAt === null) return null
        return { chargeToday: chargeToday === '1', firstChargeAt }
    } catch (e) {
        return null
    }
}

/**
 * Removes the stored record.
 *
 * @returns {void}
 */
export const clearTrialCheckoutReturn = () => {
    try {
        if (typeof localStorage === 'undefined') return
        localStorage.removeItem(TRIAL_CHECKOUT_RETURN_KEY)
    } catch (e) { /* storage blocked: nothing stored, nothing to clear */ }
}

/**
 * Stores the Checkout outcome, stamped with the time it was set.
 *
 * @param {object} details - What the route returned.
 * @param {boolean} details.chargeToday - True when the first payment is taken today.
 * @param {number|string|null} [details.firstChargeAt] - When the first payment is due (epoch seconds).
 * @returns {boolean} True when the record was stored.
 */
export const setTrialCheckoutReturn = ({ chargeToday, firstChargeAt = null }) => {
    try {
        if (typeof localStorage === 'undefined') return false
        localStorage.setItem(TRIAL_CHECKOUT_RETURN_KEY, JSON.stringify({
            chargeToday: Boolean(chargeToday),
            firstChargeAt: firstChargeAt === undefined ? null : firstChargeAt,
            createdAt: Date.now()
        }))
        return true
    } catch (e) {
        return false
    }
}

/**
 * Reads AND removes the record in one step, so a reload or a double-mounted effect
 * can never act on it twice. Anything absent, unparseable or expired reads as no record.
 *
 * @param {number} [now] - Clock override for tests.
 * @returns {{chargeToday: boolean, firstChargeAt: (number|string|null)}|null} The record, or null.
 */
export const consumeTrialCheckoutReturn = (now = Date.now()) => {
    try {
        if (typeof localStorage === 'undefined') return null
        const raw = localStorage.getItem(TRIAL_CHECKOUT_RETURN_KEY)
        if (raw === null) return null
        localStorage.removeItem(TRIAL_CHECKOUT_RETURN_KEY)
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed.createdAt !== 'number') return null
        const age = now - parsed.createdAt
        if (age < 0 || age >= TRIAL_CHECKOUT_RETURN_MAX_AGE_MS) return null
        return { chargeToday: Boolean(parsed.chargeToday), firstChargeAt: parsed.firstChargeAt ?? null }
    } catch (e) {
        return null
    }
}
